import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { extractApiErrorMessage } from '../../../../utils/apiError'
import {
  archiveTaskMaster,
  unarchiveTaskMaster,
  createTaskMaster,
  getTaskMasterDetail,
  updateTaskMaster,
} from '../api/taskMasterApi'
import { submitMasterChange } from '../../../approval/masters/api/masterApprovalApi'
import { appToast } from '../../../../components/AppToast'

function normalizeText(value) {
  const trimmed = String(value || '').trim()
  return trimmed.length > 0 ? trimmed : undefined
}

export function useTaskMasterEntryScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editingTaskMasterId = searchParams.get('id')

  const savingRef = useRef(false)

  const [form, setForm] = useState({
    task_type_name: '',
    effort_hours: '0',
    effort_minutes: '15',
    sla_hours: '8',
    default_priority: 'MEDIUM',
    skill_tags: '',
  })
  const [operationalStatus, setOperationalStatus] = useState('active')

  const [isLoading, setIsLoading] = useState(Boolean(editingTaskMasterId))
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [pendingApproval, setPendingApproval] = useState(null)

  useEffect(() => {
    let active = true

    async function loadIfEditing() {
      if (!editingTaskMasterId) return
      setIsLoading(true)
      setError('')

      try {
        const detail = await getTaskMasterDetail(editingTaskMasterId)
        if (!active) return

        const totalHours = parseFloat(detail?.default_estimated_hours ?? 0.25)
        const hours = Math.floor(totalHours)
        const minutes = Math.round((totalHours - hours) * 60)

        setForm({
          task_type_name: detail?.task_type_name || '',
          effort_hours: String(hours),
          effort_minutes: String(minutes),
          sla_hours: String(detail?.sla_hours ?? '8'),
          default_priority: detail?.default_priority || 'MEDIUM',
          skill_tags: detail?.skill_tags || '',
        })
        setOperationalStatus(Number(detail?.is_archive) === 1 ? 'inactive' : 'active')
      } catch (requestError) {
        if (!active) return
        setError(extractApiErrorMessage(requestError, 'Failed to load task master details.'))
      } finally {
        if (active) setIsLoading(false)
      }
    }

    loadIfEditing()

    return () => {
      active = false
    }
  }, [editingTaskMasterId])

  const completionPercent = useMemo(() => {
    const trackedFields = ['task_type_name', 'effort_hours', 'effort_minutes', 'sla_hours', 'default_priority']
    const filledCount = trackedFields.filter((key) => String(form[key] || '').trim().length > 0).length
    const optionalFilled = String(form.skill_tags || '').trim().length > 0 ? 1 : 0
    return Math.round(((filledCount + optionalFilled) / (trackedFields.length + 1)) * 100)
  }, [form])

  function patchForm(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  function buildPayload() {
    const h = parseInt(form.effort_hours || 0, 10)
    const m = parseInt(form.effort_minutes || 0, 10)
    const decimalHours = h + m / 60

    return {
      task_type_name: normalizeText(form.task_type_name),
      default_estimated_hours: decimalHours || 0.25,
      sla_hours: Math.max(1, Math.floor(Number(form.sla_hours) || 1)),
      default_priority: form.default_priority || 'MEDIUM',
      skill_tags: normalizeText(form.skill_tags),
      is_archive: operationalStatus === 'inactive' ? 1 : 0,
    }
  }

  async function handleSave() {
    if (savingRef.current) return
    savingRef.current = true
    setError('')
    setSuccess('')

    const cleanName = String(form.task_type_name || '').trim()
    if (!cleanName) {
      setError('Task Type Name is required.')
      savingRef.current = false
      return
    }
    if (cleanName.length < 5) {
      setError('Task Type Name must be at least 5 characters.')
      savingRef.current = false
      return
    }

    const h = parseInt(form.effort_hours || 0, 10)
    const m = parseInt(form.effort_minutes || 0, 10)
    if (Number.isNaN(h) || Number.isNaN(m) || h < 0 || m < 0 || m >= 60 || (h === 0 && m === 0)) {
      setError('Effort estimate must be greater than zero, and minutes must be between 0 and 59.')
      savingRef.current = false
      return
    }

    const slaHoursVal = Number(form.sla_hours)
    if (Number.isNaN(slaHoursVal) || slaHoursVal < 1) {
      setError('SLA Hours must be a positive integer of at least 1.')
      savingRef.current = false
      return
    }

    setIsSaving(true)

    try {
      const payload = buildPayload()
      const action = editingTaskMasterId ? 'UPDATE' : 'CREATE'

      const result = await submitMasterChange({
        module_key: 'task_master',
        action,
        entity_id: editingTaskMasterId ? Number(editingTaskMasterId) : undefined,
        payload,
      })

      const msg = result?.message || (editingTaskMasterId
        ? 'Task Master update submitted for approval.'
        : 'Task Master creation submitted for approval.')
      appToast.success(msg)
      setPendingApproval({
        requestId: result?.approval_request_id,
        message: msg,
      })
      setSuccess(msg)
      savingRef.current = false
      setIsSaving(false)
      navigate('/masters/task-master/list')
    } catch (requestError) {
      setError(extractApiErrorMessage(requestError, 'Failed to submit task master change for approval.'))
      savingRef.current = false
      setIsSaving(false)
    }
  }

  return {
    state: {
      form,
      operationalStatus,
      completionPercent,
      isLoading,
      isSaving,
      error,
      success,
      pendingApproval,
      isEditMode: Boolean(editingTaskMasterId),
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
    },
  }
}
